import { AxiosError } from 'axios'
import {
  ApiErrorSpecies,
  HttpErrorStatusCodes,
  ApiError,
  IApiErrorResponse,
  IMultipleApiError,
  ISingleApiError,
  InternalHandledErrorTypes,
  ApiResponse,
  FriendlyErrorMessage,
} from '../types'
import { errorTypesForStatusCodes, errorMessagesForErrorTypes } from '../utils'
import { IErrorResponseMiddleware } from './types'
import { registerError } from '../../features/rum/services/datadogRumService'

export default class ErrorHandlerMiddleware implements IErrorResponseMiddleware {
  public handle(error: AxiosError): IApiErrorResponse {
    registerError(error.cause ?? new Error(error.message, { cause: error.stack }), {
      code: error.code,
      status: error.status,
      baseUrl: error.config?.baseURL,
      url: error.config?.url,
      body: error.config?.data,
      response: error.response?.data,
    })

    return {
      statusCode: error.response?.status ?? HttpErrorStatusCodes.WithoutResponse,
      problem: ErrorHandlerMiddleware.getSanitizedError(error),
      headers: (error.response?.headers ?? {}) as ApiResponse['headers'],
    }
  }

  private static getSanitizedError(error: AxiosError): ApiError {
    const isErrorStandardized = this.isStandardizedErrorResponse(error.response?.data)

    if (isErrorStandardized) {
      const apiHandledError = error.response?.data as ApiError
      apiHandledError.specie = this.getErrorResponseSpecie(error.response?.data)
      return apiHandledError
    }

    return this.createGenericError(error.config?.url, error.response?.status)
  }

  private static isStandardizedErrorResponse(body: unknown): boolean {
    if (!body || typeof body !== 'object') return false

    return (
      'instance' in body &&
      'traceId' in body &&
      (this.isResponseMadeByASingleError(body) || this.isResponseMadeByMultipleErrors(body))
    )
  }

  private static getErrorResponseSpecie(body: unknown): ApiErrorSpecies {
    if (this.isResponseMadeByASingleError(body)) return ApiErrorSpecies.Single
    else return ApiErrorSpecies.Multiple
  }

  private static isResponseMadeByASingleError(body: unknown): body is ISingleApiError {
    return (
      (body as ISingleApiError).detail !== undefined &&
      (body as ISingleApiError).error !== undefined &&
      (body as ISingleApiError).type !== undefined
    )
  }

  private static isResponseMadeByMultipleErrors(body: unknown): body is IMultipleApiError {
    return Array.isArray((body as IMultipleApiError).errors)
  }

  private static createGenericError(
    path: string | undefined,
    statusCode: number | undefined,
  ): ISingleApiError {
    const type = this.getErrorTypeByStatusCode(statusCode)
    const message = errorMessagesForErrorTypes.get(type) as FriendlyErrorMessage

    return {
      instance: path ?? '',
      traceId: '-',
      type: type as string,
      error: message.title,
      detail: message.description,
      specie: ApiErrorSpecies.Single,
    }
  }

  private static getErrorTypeByStatusCode(
    statusCode: number | undefined,
  ): InternalHandledErrorTypes {
    if (statusCode === undefined) return InternalHandledErrorTypes.Unknown

    return (
      errorTypesForStatusCodes.get(statusCode as HttpErrorStatusCodes) ??
      InternalHandledErrorTypes.Unknown
    )
  }
}
